import { Copy, Eye, EyeOff, Trash } from 'react-feather';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  GetScannerQuery as GetScanner,
  GetScannersForEventQuery as GetScannersForEvent,
} from '__generated__/graphql';

import { ContainerWithOptions, Option } from '../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import DeleteScannerMutation from './DeleteScannerMutation';
import EditScannerForm from './EditScannerForm';
import GetScannerQuery from './GetScannerQuery';
import ToggleScannerMutation from './ToggleScannerMutation';
import UI from '../../../common/UI';
import useConfirmModal from '../../../common/useConfirmModal';
import useDuplicateScannerModal, { DuplicatedScanner } from './useDuplicateScannerModal';
import useLazyQuery from '../../../api/useLazyQuery';
import useLocale from '../../../common/useLocale';
import useMediaDevice from '../../../common/useMediaDevice';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';

type Event = GetScannersForEvent['project']['event'];
type Scanner = Event['scanners'][0];

interface ScannerPreviewProps {
  scanner: Scanner;
  event: Event;
}

const ScannerPreview = ({ scanner, event }: ScannerPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const project = useProject();
  const device = useMediaDevice();
  const history = useHistory();

  const [toggleScanner] = useMutation(ToggleScannerMutation, {
    variables: {
      input: {
        id: scanner.id,
        enabled: !scanner.enabled,
      },
    },
    optimisticResponse: ({ input }) => ({
      toggleScanner: {
        id: scanner.id,
        enabled: input.enabled,
        __typename: 'Scanner' as const,
      },
    }),
    onCompleted: ({ toggleScanner }) => {
      notifier.success(t(`notifications.scanner_${toggleScanner.enabled ? 'enabled' : 'disabled'}`));
    },
  });

  const [openDuplicateScannerModal] = useDuplicateScannerModal({
    scanner,
    onSuccess: (scanner: DuplicatedScanner) => {
      history.push(`/${project.id}/participants/scanners/${scanner.id}?${encodeUrl({ event: event.id })}`);
    },
  });

  const [deleteScanner] = useMutation(
    DeleteScannerMutation, {
      variables: {
        id: scanner.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.scanner_deleted'));

        history.push(`/${project.id}/participants/scanners?${encodeUrl({ event: event.id })}`);
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: scanner?.title }),
    body: t('delete_scanner_description'),
    onConfirm: () => deleteScanner(),
  }, [scanner]);

  const muted = !scanner.enabled;

  return (
    <ContainerWithOptions
      renderOptions={() => (
        <>
          <Option
            icon={<Copy />}
            label={t('duplicate')}
            onClick={() => openDuplicateScannerModal()}
          />
          <Option
            icon={<Trash />}
            label={t('delete')}
            onClick={() => confirmDelete()}
          />
        </>
      )}
    >
      <UI.GridContainer sc={{ columns: ['1fr 1fr', '3fr 1fr 1fr 1fr'], gutter: 0.5, alignVertical: 'center' }}>
        <UI.Div sc={{ muted }}>
          <UI.H4 sc={{ block: true }}>
            {scanner.title}
          </UI.H4>
        </UI.Div>

        {!device.isSmall && (
          <UI.Div sc={{ small: true, muted }} style={{ gridColumn: '2 / span 2' }}>
            <UI.Div sc={{ fontWeight: 600 }}>
              {formatInteger(scanner.check_in_count)}
              {scanner.code_count !== null && (
                <UI.Sup sc={{ muted: true }}>
                  {` / ${formatInteger(scanner.code_count)}`}
                </UI.Sup>
              )}
            </UI.Div>
            <UI.Div sc={{ ml: [1, 0] }}>
              {t('checked_in')}
            </UI.Div>
          </UI.Div>
        )}

        <UI.Div sc={{ textAlign: 'right', small: true }}>
          <UI.Toggle
            checked={scanner.enabled}
            onChange={() => toggleScanner()}
            aria-label={t(scanner.enabled ? 'disable_name' : 'enable_name', {
              name: scanner.title,
            })}
          />

          <UI.Truncate sc={{ color: 'gray.500' }}>
            <UI.Icon>{scanner.enabled ? <Eye /> : <EyeOff />}</UI.Icon>
            {' '}
            {scanner.enabled ? t('enabled') : t('disabled')}
          </UI.Truncate>
        </UI.Div>
      </UI.GridContainer>
    </ContainerWithOptions>
  );
};

export interface ScannerCardProps {
  scanner: Scanner;
  event: Event;
  active?: boolean;
  muted?: boolean;
}

const ScannerCard = ({
  scanner: preview, event, active = false, muted = false,
}: ScannerCardProps) => {
  const project = useProject();

  const [fetch, { data }] = useLazyQuery<GetScanner>(
    GetScannerQuery,
    {
      variables: {
        project_id: project.id,
        scanner_id: preview.id,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (active) {
      fetch();
    }
  }, [active]);

  const scanner = data?.project.scanner;

  return (
    <UI.CollapsibleNavCard
      to={`/${project.id}/participants/scanners/${!active ? preview.id : ''}?${encodeUrl({ event: event.id })}`}
      open={active}
      muted={muted}
      preview={(
        <ScannerPreview scanner={{ ...preview, ...scanner }} event={event} />
      )}
      body={
        scanner
          ? <EditScannerForm scanner={scanner} />
          : <UI.PageLoader />
      }
      id={`ScannerCard_${preview.id}`}
      ariaLabel={preview.title}
    />
  );
};

export default ScannerCard;
